import React from 'react';
import { WeekDayHeader, WeekBlock, EWeekBlockStatus } from '../types/agenda.types';
import { PX_PER_HOUR } from '../utils/weekBlockUtils';
import './WeekScheduleView.scss';

interface WeekScheduleViewProps {
  dayHeaders: WeekDayHeader[];
  blocks: WeekBlock[];
  hourStart?: number;
  hourEnd?: number;
  onBlockClick?: (block: WeekBlock) => void;
  onDayClick?: (dayIndex: number) => void;
}

// Clase (colores del tag del estado, ver getStatusInfo) y texto del estado de cada bloque
const STATUS_MAP: Record<EWeekBlockStatus, { className: string; label: string }> = {
  [EWeekBlockStatus.SCHEDULED]: { className: 'week-grid__block--neutral', label: 'Agendado' },
  [EWeekBlockStatus.CONFIRMED]: { className: 'week-grid__block--ok', label: 'Confirmado' },
  [EWeekBlockStatus.WAITING]: { className: 'week-grid__block--warn', label: 'En espera' },
  [EWeekBlockStatus.IN_ATTENTION]: { className: 'week-grid__block--selected', label: 'En atención' },
  [EWeekBlockStatus.COMPLETED]: { className: 'week-grid__block--ok', label: 'Atendido' },
  [EWeekBlockStatus.MISSED]: { className: 'week-grid__block--neutral week-grid__block--missed', label: 'Ausente' },
  [EWeekBlockStatus.EXCLUSION]: { className: 'week-grid__block--exclusion', label: 'Excluido' },
  [EWeekBlockStatus.FREE]: { className: 'week-grid__block--free', label: 'Libre' },
};

const formatHour = (h: number): string => h.toString().padStart(2, '0');

const WeekScheduleView: React.FC<WeekScheduleViewProps> = ({
  dayHeaders,
  blocks,
  hourStart = 8,
  hourEnd = 14,
  onBlockClick,
  onDayClick,
}) => {
  const hours = Array.from({ length: hourEnd - hourStart }, (_, i) => hourStart + i);

  return (
    // El alto de cada hora sale de PX_PER_HOUR, el mismo que posiciona los bloques
    <div className="week-grid" style={{ '--week-hour-height': `${PX_PER_HOUR}px` } as React.CSSProperties}>
      {/* Header */}
      <div className="week-grid__head">
        <div className="week-grid__head-cell" />
        {dayHeaders.map((header, idx) => (
          <div
            key={idx}
            className={`week-grid__head-cell${header.isToday ? ' week-grid__head-cell--today' : ''}`}
            aria-current={header.isToday ? 'date' : undefined}
          >
            <span className="week-grid__day-name">{header.dayName}</span>
            <span className="week-grid__day-number">{header.dayNumber}</span>
          </div>
        ))}
      </div>

      {/* Body */}
      <div className="week-grid__body">
        <div className="week-grid__gutter">
          {hours.map((h) => (
            <div key={h} className="week-grid__hour">
              {formatHour(h)}
            </div>
          ))}
        </div>

        {dayHeaders.map((header, colIdx) => (
          <div
            key={colIdx}
            className={`week-grid__col${header.isToday ? ' week-grid__col--today' : ''}`}
          >
            {blocks
              .filter((block) => block.dayIndex === colIdx)
              .map((block, blockIdx) => {
                const status = STATUS_MAP[block.status];
                const tooltip = [block.sublabel, block.label, status.label].filter(Boolean).join(' · ');
                return (
                  <div
                    key={blockIdx}
                    className={`week-grid__block nv-tip ${status.className}`}
                    style={{ top: block.topPx, height: block.heightPx }}
                    data-pr-tooltip={tooltip}
                    onClick={() =>
                      block.appointmentId
                        ? onBlockClick?.(block)
                        : onDayClick?.(block.dayIndex)
                    }
                  >
                    <span className="week-grid__block-name">{block.label}</span>
                    {block.sublabel && <small className="week-grid__block-time">{block.sublabel}</small>}
                    <span className="nv-sr-only">{status.label}</span>
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeekScheduleView;
